import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdEdit, MdDelete } from 'react-icons/md';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { BASE_URL } from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const BranchMaster = () => {
  /* ---------- STATE ---------- */
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const [form, setForm] = useState({
    branchname: '',
    branchcode: '',
    branchtype: 'Main Branch',
    parentbranchid: '',
    status: true,
  });

  /* ---------- HELPERS ---------- */
  const token = () => localStorage.getItem('token');
  const headers = () => ({
    'Content-Type': 'application/json',
    ...(token() && { Authorization: `Bearer ${token()}` }),
  });

  /* ---------- API CALLS ---------- */
  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/branch`, { headers: headers() });
      const { success, data } = await res.json();
      if (success) setBranches(data);
    } catch (e) {
      toast.error('Failed to connect to server');
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token()) fetchAll();
  }, []);

  /* ---------- SEARCH & PAGINATION ---------- */
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return branches.filter(b =>
      b.branchname?.toLowerCase().includes(term) ||
      b.branchcode?.toLowerCase().includes(term)
    );
  }, [branches, search]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  /* ---------- HANDLERS ---------- */
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        branchname: form.branchname.trim(),
        branchcode: form.branchcode.trim(),
        ...(form.branchtype === 'Main Branch' && { parentbranchid: null })
      };

      const url = editingId ? `${API_BASE_URL}/branch/${editingId}` : `${API_BASE_URL}/branch`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success(editingId ? 'Branch updated' : 'Branch created');
        fetchAll();
        setModalOpen(false);
      }
    } catch (err) {
      toast.error('Operation failed');
      setError('Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    const res = await fetch(`${API_BASE_URL}/branch/${id}`, { method: 'DELETE', headers: headers() });
    if(res.ok) toast.success('Branch deleted');
    fetchAll();
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer />
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Branch Master</h2>

      <div className="bg-white rounded-xl shadow p-6">
        {/* Toolbar */}
        <div className="flex flex-wrap gap-4 mb-6 justify-between">
          <input
            type="text"
            placeholder="Search branch name or code..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="px-4 py-2 border rounded-md w-80 focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <button
            onClick={() => {
              setEditingId(null);
              setForm({ branchname: '', branchcode: '', branchtype: 'Main Branch', parentbranchid: '', status: true });
              setModalOpen(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition font-medium"
          >
            Add New Branch
          </button>
        </div>

        {/* Table - Styled like Pincode List */}
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="p-3 text-left">S.No</th>
                <th className="p-3 text-left">Branch Name</th>
                <th className="p-3 text-left">Branch Code</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Parent Branch</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-8">Loading...</td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-8 text-gray-500">No branches found</td></tr>
              ) : (
                paginated.map((b, i) => {
                  // S.No logic: Fixes search indexing
                  const sNo = search
                    ? branches.findIndex(orig => orig._id === b._id) + 1
                    : (page - 1) * perPage + i + 1;

                  return (
                    <tr key={b._id} className="border-b hover:bg-gray-50 transition">
                      <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                      <td className="p-3 font-semibold text-gray-800">{b.branchname}</td>
                      <td className="p-3">{b.branchcode}</td>
                      <td className="p-3">{b.branchtype}</td>
                      <td className="p-3">{b.parentbranchid?.branchname || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${b.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {b.status ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => {
                              setEditingId(b._id);
                              setForm({
                                branchname: b.branchname,
                                branchcode: b.branchcode,
                                branchtype: b.branchtype,
                                parentbranchid: b.parentbranchid?._id || '',
                                status: b.status,
                              });
                              setModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <MdEdit size={20} />
                          </button>
                          <button onClick={() => remove(b._id)} className="text-red-600 hover:text-red-800">
                            <MdDelete size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - Styled like Pincode List */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div className="text-sm text-gray-600">
              Showing {Math.min(filtered.length, (page - 1) * perPage + 1)} to {Math.min(filtered.length, page * perPage)} of {filtered.length} branches
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50 hover:bg-gray-300 transition"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50 hover:bg-gray-300 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal - Styled for Consistency */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-800">{editingId ? 'Edit Branch' : 'Add New Branch'}</h2>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Branch Name *</label>
                <input required value={form.branchname} onChange={e => setForm({ ...form, branchname: e.target.value })} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Branch Code *</label>
                <input required value={form.branchcode} onChange={e => setForm({ ...form, branchcode: e.target.value })} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Branch Type</label>
                <select value={form.branchtype} onChange={e => setForm({ ...form, branchtype: e.target.value, parentbranchid: '' })} className="w-full px-4 py-2 border rounded-lg outline-none">
                  <option value="Main Branch">Main Branch</option>
                  <option value="Sub Branch">Sub Branch</option>
                </select>
              </div>

              {form.branchtype === 'Sub Branch' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Parent Branch *</label>
                  <select
                    required
                    value={form.parentbranchid}
                    onChange={e => setForm({ ...form, parentbranchid: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg outline-none"
                  >
                    <option value="">Select Main Branch</option>
                    {branches
                      .filter(b => b.branchtype === 'Main Branch')
                      .sort((a, b) => a.branchname.localeCompare(b.branchname))
                      .map(b => <option key={b._id} value={b._id}>{b.branchname}</option>)
                    }
                  </select>
                </div>
              )}

              <div className="flex items-center pt-2">
                <input type="checkbox" id="status" checked={form.status} onChange={e => setForm({ ...form, status: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
                <label htmlFor="status" className="ml-2 text-sm font-medium text-gray-700 cursor-pointer">Branch is Active</label>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button type="button" onClick={() => setModalOpen(false)} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition">Cancel</button>
                <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                  {loading ? 'Processing...' : editingId ? 'Update Branch' : 'Create Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchMaster;